import * as React from "react";
import { values } from "lodash";

import { QuestionnaireContainerProps } from "./QuestionnaireContainerProps";
import { QuestionnaireSectionProps } from "./QuestionnaireSectionProps";
import { QuestionProps, AnsweredQuestion, QuestionMap } from "./model";
import { isQuestionDisabled } from "./questionnaireConfig/models";

export function renderQuestionnaireSections(
    containerProps: Pick<QuestionnaireContainerProps, "questionnaireKey" | "readonly" | "extraQuestionContent">,
    children: React.ReactNode
): (React.ReactNode | React.ReactNode[])[] {
    return React.Children
        .toArray(children)
        .filter(c => c)
        .map<React.ReactNode | React.ReactNode[]>((child, index) => {
            const element = child as React.ReactElement;
            return renderQuestionnaireSection(element, containerProps, index);
        });
}

const renderQuestionnaireSection = (
    element: React.ReactElement,
    props: Pick<QuestionnaireContainerProps, "questionnaireKey" | "readonly" | "extraQuestionContent">,
    index: number
): any => {
    // renderQuestionnaireSection is called recursively so defend against it going too deep
    if (elementIsQuestion(element)) {
        return element;
    }
    if (typeof element.type === "string") {
        // These are DOM elements so don't pass the extra props but do keep traversing
        return React.cloneElement(element, {
            ...element.props,
            children: element.props.children && renderQuestionnaireSections(props, element.props.children)
        });
    }
    if (element && element.props) {
        return React.cloneElement<QuestionnaireSectionProps>(element, {
            ...element.props,
            questionnaireKey: props.questionnaireKey,
            index: element.props.index ?? index,
            readonly: props.readonly,
            extraQuestionContent: props.extraQuestionContent,
            children: element.props.children && renderQuestionnaireSections(props, element.props.children)
        });
    }

    return element;
};

export function isSectionVisible(section: { questions: QuestionMap; neverHide?: boolean }) {
    let notAllQuestionsRemoved = true;
    if (section.questions) {
        const questions = values(section.questions);
        notAllQuestionsRemoved = questions.some(q => !q.isRemovedByUser);
    }
    return section.neverHide === true || (Object.keys(section.questions || {}).length !== 0 && notAllQuestionsRemoved);
}

export function registerQuestionnaireSection(
    props: Pick<QuestionnaireSectionProps, "id" | "active" | "questionnaireKey" | "index" | "title" | "neverHide" | "registerSection">) {
    props.registerSection({
        sectionId: props.id,
        active: props.active,
        questionnaireKey: props.questionnaireKey,
        index: props.index,
        title: props.title,
        neverHide: !!props.neverHide
    });
}

export function registerQuestionnaireSectionIndexIfNeeded(props: QuestionnaireSectionProps) {
    props.registerSectionIndexIfNeeded({
        sectionId: props.id,
        questionnaireKey: props.questionnaireKey,
        index: props.index,
    });
}

export function registerQuestionsForSection(props: Pick<QuestionnaireSectionProps, "children" | "id" | "questions" | "questionnaireKey" | "removeQuestion" | "updateAnswer">) { // eslint-disable-line max-len
    const questionsOnScreenOrRemoved = registerQuestionsForElement(props.children, props);

    const questionsToRemove = Object.keys(props.questions).filter(id => questionsOnScreenOrRemoved.indexOf(id) === -1);
    questionsToRemove.forEach(id => props.removeQuestion({ sectionId: props.questions[id].sectionId, questionnaireKey: props.questionnaireKey, id }));
}

function registerQuestionsForElement(
    children: React.ReactNode,
    props: Pick<QuestionnaireSectionProps, "id" | "questions" | "questionnaireKey" | "removeQuestion" | "updateAnswer">
) {
    const questionsOnScreenOrRemoved: string[] = [];
    React.Children.forEach(children, child => {
        const element = child as React.ReactElement<QuestionProps>;
        const elementWithChildren = child as React.ReactElement<any>;
        if (elementIsQuestion(element)) {
            const { questionConfig, answer, mandatory, showError } = element.props;
            const { id } = questionConfig;
            const existingQuestion = props.questions[id];
            if (isQuestionDisabled(questionConfig) && existingQuestion) {
                props.removeQuestion({ sectionId: props.id, questionnaireKey: props.questionnaireKey, id });
            } else if (!existingQuestion && !isQuestionDisabled(questionConfig)) {
                const answeredQuestion = { id, question: questionConfig.questionText, answer, isValid: !mandatory, showError };
                props.updateAnswer({ sectionId: props.id, id, question: answeredQuestion, questionnaireKey: props.questionnaireKey });
            }
            questionsOnScreenOrRemoved.push(id);
        } else if (elementWithChildren && elementWithChildren.props && elementWithChildren.props.children) {
            questionsOnScreenOrRemoved.push(
                ...registerQuestionsForElement(elementWithChildren.props.children, props)
            );
        }
    });

    return questionsOnScreenOrRemoved;
}

export function renderQuestions(props: Pick<QuestionnaireSectionProps, "id" | "questionnaireKey" | "children" | "questions" | "extraQuestionContent" | "isComplete" | "updateAnswer" | "validateQuestion">) { // eslint-disable-line max-len
    return React.Children.map(props.children, child => addPropsToChildren(child, props));
}

function addPropsToChildren(
    child: React.ReactNode,
    props: Pick<QuestionnaireSectionProps, "id" | "questionnaireKey" | "children" | "questions" | "extraQuestionContent" | "isComplete" | "updateAnswer" | "validateQuestion"> // eslint-disable-line max-len
): React.ReactElement<any> {
    const element = child as any;
    if (!elementIsQuestion(element)) {
        if (element && element.props && element.props.children) {
            return React.cloneElement(element, {
                ...element.props,
                children: React.Children.map(element.props.children, c => addPropsToChildren(c, props))
            });
        }
        return element;
    }
    const questionId = element.props.questionConfig.id;
    const answeredQuestion = props.questions[questionId];
    const answer = getAnswerForQuestion(answeredQuestion, element.props);
    const combinedExtraContent = () => {
        const parentFormContainerExtraContent = props.extraQuestionContent && props.extraQuestionContent[questionId];
        const elementOwnExtraContent = element.props.extraContent;
        return (
            <>
                {parentFormContainerExtraContent}
                {elementOwnExtraContent}
            </>
        );
    };
    const newProps: QuestionProps = {
        ...element.props,
        answer,
        showError: props.isComplete || element.props.showError,
        extraContent: combinedExtraContent(),
        updateQuestion: (question: AnsweredQuestion) =>
            props.updateAnswer({ sectionId: props.id, id: questionId, question, questionnaireKey: props.questionnaireKey }),
        validateQuestion: (validatedQuestion: Promise<AnsweredQuestion>) => {
            const payload = { validatedQuestion, id: questionId, sectionId: props.id, questionnaireKey: props.questionnaireKey };
            props.validateQuestion(payload);
        }
    };
    return React.cloneElement(element, newProps);
}

function elementIsQuestion(element: React.ReactElement<QuestionProps>): element is React.ReactElement<QuestionProps> {
    return !!(element && element.props && element.props.questionConfig);
}

const getAnswerForQuestion = (answeredQuestion: AnsweredQuestion, props: QuestionProps) => {
    if (answeredQuestion && (answeredQuestion.answer || answeredQuestion.answer === 0)) {
        return answeredQuestion.answer;
    } else if (props.answer || props.answer === 0) {
        return props.answer;
    } else {
        return "";
    }
};
